import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Heart, 
  LayoutDashboard, 
  FolderKanban, 
  Users, 
  Building2, 
  BarChart3, 
  FileText,
  UserCog,
  X,
  Receipt,
  Settings,
  Calendar,
  ClipboardList,
  Rocket
} from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sidebar = ({ isOpen, onClose }) => {
  const { user, getAuthHeaders } = useAuth();
  const [tenantLogo, setTenantLogo] = useState(null);
  const [tenantName, setTenantName] = useState(null);
  const [tenantBusinessType, setTenantBusinessType] = useState(null);

  const isSuperAdmin = user?.role === 'super_admin';

  // Fetch tenant info for branding when user is not super admin
  useEffect(() => {
    const fetchTenantBranding = async () => {
      if (!isSuperAdmin && user?.organization_id) {
        try {
          const res = await axios.get(`${API_URL}/tenants/${user.organization_id}`, getAuthHeaders());
          if (res.data) {
            setTenantName(res.data.name);
            setTenantBusinessType(res.data.business_type || 'web_marketing');
            if (res.data.logo) {
              setTenantLogo(`data:${res.data.logo_content_type || 'image/png'};base64,${res.data.logo}`);
            }
          }
        } catch (error) {
          console.error('Failed to fetch tenant branding:', error);
        }
      }
    };
    fetchTenantBranding();
  }, [user, isSuperAdmin, getAuthHeaders]);

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['super_admin', 'admin', 'team', 'client', 'client_team'] },
    { to: '/projects', icon: FolderKanban, label: 'Projects', roles: ['super_admin', 'admin', 'team', 'client', 'client_team'] },
    { to: '/calendar', icon: Calendar, label: 'Calendar', roles: ['super_admin', 'admin', 'team', 'client', 'client_team'] },
    { to: '/clients', icon: Users, label: 'Clients', roles: ['super_admin', 'admin', 'team'] },
    { to: '/tenants', icon: Building2, label: 'Tenants', roles: ['super_admin'] },
    { to: '/meetings', icon: Calendar, label: 'Meetings', roles: ['super_admin'] },
    // { to: '/seo', icon: BarChart3, label: 'SEO Analytics', roles: ['super_admin', 'admin', 'team'] }, // Hidden for now
    { to: '/invoices', icon: FileText, label: 'Invoices', roles: ['super_admin', 'admin', 'team'] },
    { to: '/billing', icon: Receipt, label: 'Recurring Billing', roles: ['super_admin'] },
    { to: '/users', icon: UserCog, label: 'User Management', roles: ['super_admin', 'admin'] },
    // Forms menu - only for tenant users (admin/team), not super admin
    { to: '/forms', icon: ClipboardList, label: 'Forms', roles: ['admin', 'team'] },
    { to: '/my-settings', icon: Settings, label: 'Business Settings', roles: ['admin', 'team'] },
    { to: '/and-go', icon: Rocket, label: 'and...Go', roles: ['super_admin', 'admin', 'team', 'client', 'client_team'] },
  ];

  const filteredNavItems = navItems.filter(item => item.roles.includes(user?.role));

  // Determine branding to show
  const displayName = (!isSuperAdmin && tenantName) ? tenantName : 'My Client PM';

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-slate-100">
        <div className="flex items-center gap-2">
          {tenantLogo && !isSuperAdmin ? (
            <img 
              src={tenantLogo} 
              alt={tenantName} 
              className="w-8 h-8 object-contain rounded"
              data-testid="tenant-logo"
            />
          ) : (
            <Heart className="w-7 h-7 text-slate-900 fill-slate-900 heart-logo" />
          )}
          <span className="text-lg font-bold text-slate-900 truncate max-w-[160px]" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            {displayName}
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {filteredNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onClose}
            className={({ isActive }) => 
              `sidebar-link ${isActive ? 'active' : ''}`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
          >
            <item.icon size={20} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* App version */}
      <div className="p-4 border-t border-slate-100">
        <p className="text-xs text-slate-400 text-center">
          {!isSuperAdmin && tenantName ? tenantName : 'My Client PM'} v1.0
        </p>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="sidebar" data-testid="desktop-sidebar">
        <SidebarContent />
      </aside>

      {/* Mobile overlay */}
      <div 
        className={`mobile-menu-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
      />

      {/* Mobile sidebar */}
      <aside className={`mobile-sidebar ${isOpen ? 'open' : ''}`} data-testid="mobile-sidebar">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            {tenantLogo && !isSuperAdmin ? (
              <img src={tenantLogo} alt={tenantName} className="w-6 h-6 object-contain rounded" />
            ) : (
              <Heart className="w-6 h-6 text-slate-900 fill-slate-900" />
            )}
            <span className="font-bold text-slate-900 truncate max-w-[120px]">
              {!isSuperAdmin && tenantName ? tenantName : 'OPM'}
            </span>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-mobile-menu">
            <X size={20} />
          </Button>
        </div>
        <div className="flex flex-col h-[calc(100%-57px)]">
          <SidebarContent />
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
